import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { i18n } from '@/i18n'
import { parseCallableError } from '@/lib/firebase/errors'
import {
  MAGIC_LINK_EMAIL_STORAGE_KEY,
  completeMagicLink as repoCompleteMagicLink,
  createWorkspace as repoCreateWorkspace,
  observeAuthState,
  readClaims,
  refreshAuthClaims as repoRefreshAuthClaims,
  sendMagicLink as repoSendMagicLink,
  signInWithGoogle as repoSignInWithGoogle,
  signOut as repoSignOut,
  updateUserProfile as repoUpdateUserProfile,
} from '@/repositories/auth'
import type { AuthClaims, AuthUser } from '@/types/auth'
import type { SupportedLocale } from '@/i18n'

export interface CreateWorkspaceInput {
  workspaceName: string
  brandName: string
  contentLanguage: SupportedLocale
}

function magicLinkContinueUrl(): string {
  return `${window.location.origin}/auth/callback`
}

const CLAIM_RETRY_DELAYS_MS = [1000, 1500, 2000]

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export type AuthAction =
  | 'sendMagicLink'
  | 'completeMagicLink'
  | 'signInWithGoogle'
  | 'signOut'
  | 'refreshClaims'
  | 'createWorkspace'
  | 'updateProfile'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<AuthUser | null>(null)
  const claims = ref<AuthClaims | null>(null)
  const pendingAction = ref<AuthAction | null>(null)
  const status = ref<'unknown' | 'signed-out' | 'signed-in'>('unknown')
  const error = ref<string | null>(null)

  const isSignedIn = computed(() => status.value === 'signed-in')
  const hasWorkspace = computed(() => claims.value?.workspaceId !== undefined)
  const isEntitled = computed(() => claims.value?.entitled === true)

  let initPromise: Promise<void> | null = null

  async function loadClaimsForCurrentUser(forceRefresh = false): Promise<void> {
    if (!user.value) {
      claims.value = null
      return
    }
    claims.value = await readClaims(forceRefresh)
  }

  function init(): Promise<void> {
    if (initPromise) return initPromise

    initPromise = new Promise((resolve) => {
      let resolved = false
      function finish(): void {
        if (!resolved) {
          resolved = true
          resolve()
        }
      }

      try {
        observeAuthState((nextUser: AuthUser | null) => {
          void (async () => {
            user.value = nextUser
            status.value = nextUser ? 'signed-in' : 'signed-out'
            try {
              await loadClaimsForCurrentUser()
            } catch (cause) {
              error.value = parseCallableError(cause).message
            }
            finish()
          })()
        })
      } catch (cause) {
        status.value = 'signed-out'
        error.value = parseCallableError(cause).message
        finish()
      }
    })

    return initPromise
  }

  function runAction<T>(name: AuthAction, action: () => Promise<T>): Promise<T> {
    error.value = null
    pendingAction.value = name
    return action()
      .catch((cause: unknown) => {
        error.value = parseCallableError(cause).message
        throw cause
      })
      .finally(() => {
        if (pendingAction.value === name) pendingAction.value = null
      })
  }

  function isPending(name: AuthAction): boolean {
    return pendingAction.value === name
  }

  function sendMagicLink(email: string): Promise<void> {
    return runAction('sendMagicLink', () => repoSendMagicLink(email, magicLinkContinueUrl()))
  }

  function completeMagicLink(url: string, email?: string): Promise<void> {
    return runAction('completeMagicLink', async () => {
      const resolvedEmail = email ?? window.localStorage.getItem(MAGIC_LINK_EMAIL_STORAGE_KEY)
      if (!resolvedEmail) {
        throw new Error(i18n.global.t('auth.errors.noEmailOnFile'))
      }
      await repoCompleteMagicLink(url, resolvedEmail)
    })
  }

  function signInWithGoogle(): Promise<void> {
    return runAction('signInWithGoogle', async () => {
      await repoSignInWithGoogle()
    })
  }

  function signOut(): Promise<void> {
    return runAction('signOut', async () => {
      await repoSignOut()
      user.value = null
      claims.value = null
      status.value = 'signed-out'
    })
  }

  function refreshClaims(): Promise<void> {
    return runAction('refreshClaims', async () => {
      await repoRefreshAuthClaims({})
      await loadClaimsForCurrentUser(true)
    })
  }

  function createWorkspace(input: CreateWorkspaceInput): Promise<void> {
    return runAction('createWorkspace', async () => {
      await repoCreateWorkspace(input)
      await repoRefreshAuthClaims({})
      await loadClaimsForCurrentUser(true)

      for (const waitMs of CLAIM_RETRY_DELAYS_MS) {
        if (claims.value?.workspaceId) return
        await delay(waitMs)
        await repoRefreshAuthClaims({})
        await loadClaimsForCurrentUser(true)
      }
    })
  }

  function updateProfile(fullName: string): Promise<void> {
    return runAction('updateProfile', async () => {
      await repoUpdateUserProfile({ fullName })
      if (user.value) user.value = { ...user.value, displayName: fullName }
    })
  }

  return {
    user,
    claims,
    status,
    error,
    pendingAction,
    isPending,
    isSignedIn,
    hasWorkspace,
    isEntitled,
    init,
    sendMagicLink,
    completeMagicLink,
    signInWithGoogle,
    signOut,
    refreshClaims,
    createWorkspace,
    updateProfile,
  }
})
